import { HOUSE_STYLE_V1, HouseStyle, cleanColorUse } from './houseStyle';
import { findRecipe } from './recipes';

/**
 * The brief people paste into their own AI tool (Claude, ChatGPT, Gemini…)
 * before describing a section. It carries the FF house style and the rules
 * that make the reply paste cleanly into Genesis. The rules also work with
 * widgets saved before the v2 scoper, so the brief is safe to use anywhere.
 */

export { HOUSE_STYLE_V1 } from './houseStyle';

/** A section already on the page whose look the new one should match. */
export type MatchSection = { label: string; html: string; css: string };

export type BriefOptions = {
  houseStyle?: HouseStyle;
  recipeId?: string | null;
  match?: MatchSection | null;
};

// Keeps the brief pasteable: AI tools accept long prompts, but a huge
// reference section crowds out the actual request.
const MAX_MATCH_CHARS = 24000;
const clip = (text: string, max: number) => (text.length > max ? `${text.slice(0, max)}\n/* … trimmed … */` : text);

export type BriefTarget = 'ai-widget' | 'custom-widget';

/** The line the person replaces with their request (the built-in generator sends the request separately). */
export const BRIEF_REQUEST_PLACEHOLDER = '[Describe the section here: its purpose, the text, and the image URLs to use.]';

/** The "Layout" lines for a recipe, ending with a blank line; none for an unknown recipe. */
export const recipeLines = (recipeId?: string | null): string[] => {
  const recipe = findRecipe(recipeId);
  return recipe ? [`Layout (${recipe.label})`, `- ${recipe.layout}`, ''] : [];
};

/** The "Match this section" lines, with its code trimmed so it can't crowd out the request. */
export const matchLines = (match: MatchSection): string[] => {
  const half = Math.floor(MAX_MATCH_CHARS / 2);
  return [
    `Match this section (“${match.label}”)`,
    '- It is already on the page. Reuse its spacing, type sizes, colors and button style so the two look like one design. Don’t copy its content.',
    '```html',
    clip(match.html || '', half),
    '```',
    '```css',
    clip(match.css || '', half),
    '```'
  ];
};

export const buildAiBrief = (target: BriefTarget = 'ai-widget', options: BriefOptions = {}): string => {
  const hs = options.houseStyle || HOUSE_STYLE_V1;
  const base = target === 'custom-widget' ? '.custom-widget-root' : '.ai-widget-root';
  const colors = hs.colors.map(c => `  var(--gx-${c.token}, ${c.value}) ${cleanColorUse(c.use)}`).join('\n');
  const lines = [
    'I’m building one section of a page on ff.com (Faraday Future) in our CMS, Genesis. Write it as plain HTML, CSS and JavaScript.',
    '',
    'What the section should contain:',
    BRIEF_REQUEST_PLACEHOLDER,
    '',
    ...recipeLines(options.recipeId),
    'How to reply',
    '- Give exactly three code blocks: ```html, ```css and ```js (the js block can be empty).',
    '- The html block holds only this section’s markup: no <html>, <head>, <body>, <style>, <script> or <link> tags.',
    '',
    `House style (FF v${hs.version})`,
    `- Fonts: font-family: var(--gx-font, ${hs.fontEn}); for Chinese text use var(--gx-font-zh, ${hs.fontZh}).`,
    '- Colors: always write them with the fallback shown, and don’t add other colors unless I ask:',
    colors,
    `- Buttons are pills: border-radius: var(--gx-radius-button, ${hs.radiusButton}). Cards and images: border-radius: var(--gx-radius-card, ${hs.radiusCard}).`,
    '- Clean, product-led layouts with generous whitespace, like ff.com. Mobile first; it must work from 360px to 1920px wide.',
    '',
    'CSS rules',
    `- Start every selector with ${base}, for example ${base} .hero h1 { … }. Put CSS variables on ${base}, not :root.`,
    '- Don’t style html or body, don’t use position: fixed and don’t use 100vh heights: this is one section of a longer page.',
    '- @keyframes animations and a Google Fonts @import are fine.',
    '',
    'JavaScript rules',
    '- A variable named root already holds this section’s outer element. Find elements with root.querySelector(…).',
    '- The page has already loaded when the script runs, so don’t wait for DOMContentLoaded.',
    '- Add event listeners in the script; no onclick="…" or other on… attributes in the HTML.',
    '- No libraries, CDN scripts, Tailwind, frameworks, fetch calls, eval, cookies or modules (import/export).',
    '',
    'Images',
    '- Use only the image URLs I give you (they’re on genesis-cdn.ff.com). Never embed base64 images or use stock or placeholder image sites.',
    '- If you need an image I haven’t given you, leave src empty and mark it with data-gx-field so I can add it.',
    '',
    'Editable content',
    '- Add data-gx-field="name" to every piece of text, link and image someone may want to change later, e.g. data-gx-field="heading", "cta", "photo".',
    '- Use short, unique, lowercase names; for repeated items use names like "step-1-title".'
  ];
  if (options.match) {
    lines.push('', ...matchLines(options.match));
  }
  if (target === 'custom-widget') {
    lines.push(
      '',
      'Props (only if I list them)',
      '- Write each prop in the HTML as {{propName}}; for a list, wrap the repeated markup in {{#items}}…{{/items}} and use {{field}} inside.',
      '- In the script, prop values are in __WIDGET_PROPS__.'
    );
  }
  return lines.join('\n');
};
